import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { ArrowLeft, Save, User, Users, AlertTriangle } from "lucide-react";
import { generateClient } from "aws-amplify/api";
import { getEvent, listEventStandings, listUsers } from "../../graphql/queries";
import { updateEventStanding } from "../../graphql/mutations";

const client = generateClient();

interface UnassignedStanding {
  id: string;
  rank: number;
  points: number;
  omw: number;
  gw: number;
  ogw: number;
  playerName?: string;
}

const EventStandingsAssignment: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedUsers, setSelectedUsers] = useState<Record<string, string>>(
    {}
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch event details
  const {
    data: event,
    isLoading: eventLoading,
    error: eventError,
  } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getEvent,
        variables: { id: eventId! },
      });
      return result.data.getEvent;
    },
    enabled: !!eventId,
  });

  // Fetch all users for assignment
  const {
    data: allUsers,
    isLoading: usersLoading,
    error: usersError,
  } = useQuery({
    queryKey: ["allUsers"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listUsers,
        variables: { limit: 1000 },
      });
      return result.data.listUsers.items;
    },
  });

  // Fetch existing standings
  const {
    data: existingStandings,
    isLoading: standingsLoading,
    error: standingsError,
  } = useQuery({
    queryKey: ["eventStandings", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventStandings,
        variables: {
          filter: { eventId: { eq: eventId } },
          limit: 100,
        },
      });
      return result.data.listEventStandings.items;
    },
    enabled: !!eventId,
  });

  // Filter unassigned standings (those without userId or with invalid userId)
  const unassignedStandings =
    existingStandings?.filter(
      (standing: any) =>
        !standing.userId ||
        standing.userId === "" ||
        standing.userId === "unassigned"
    ) || [];

  // Handle user selection
  const handleUserSelection = (standingId: string, userId: string) => {
    setSelectedUsers((prev) => ({
      ...prev,
      [standingId]: userId,
    }));
  };

  // Save assignments mutation
  const saveAssignmentsMutation = useMutation({
    mutationFn: async (
      assignments: { standingId: string; userId: string }[]
    ) => {
      const promises = assignments.map(({ standingId, userId }) => {
        const standing = existingStandings?.find(
          (s: any) => s.id === standingId
        );
        if (!standing) return Promise.resolve();

        return client.graphql({
          query: updateEventStanding,
          variables: {
            input: {
              id: standingId,
              eventId: eventId!,
              userId: userId,
              rank: standing.rank,
              points: standing.points,
              omw: standing.omw,
              gw: standing.gw,
              ogw: standing.ogw,
            },
          },
        });
      });

      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eventStandings", eventId] });
      setIsSubmitting(false);
      alert("User assignments saved successfully!");
      navigate(`/admin/events/${eventId}/standings`);
    },
    onError: (error: any) => {
      console.error("Error saving assignments:", error);
      setIsSubmitting(false);
      alert("Failed to save assignments. Please try again.");
    },
  });

  const handleSaveAssignments = () => {
    const assignments = Object.entries(selectedUsers)
      .filter(([_, userId]) => userId && userId !== "")
      .map(([standingId, userId]) => ({ standingId, userId }));

    if (assignments.length === 0) {
      alert(
        "No user assignments to save. Please select users for the standings."
      );
      return;
    }

    setIsSubmitting(true);
    saveAssignmentsMutation.mutate(assignments);
  };

  const assignedCount = Object.keys(selectedUsers).length;
  const totalUnassigned = unassignedStandings.length;

  if (eventLoading || usersLoading || standingsLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (eventError || usersError || standingsError || !event) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center text-red-600">Error loading event data</div>
      </div>
    );
  }

  if (unassignedStandings.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/admin/events/${eventId}/standings`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Standings
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Assign Users to Standings</h1>
            <p className="text-muted-foreground mt-2">
              All standings have been assigned users
            </p>
          </div>
        </div>

        <Card>
          <CardContent className="p-8 text-center">
            <Users className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">All Users Assigned!</h3>
            <p className="text-muted-foreground mb-4">
              All standings for this event have been assigned to users.
            </p>
            <Button
              onClick={() => navigate(`/admin/events/${eventId}/standings`)}
            >
              View Standings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/admin/events/${eventId}/standings`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Standings
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">Assign Users to Standings</h1>
          <p className="text-muted-foreground mt-2">
            Assign users to unassigned standings for {event.name}
          </p>
        </div>
        <Button
          onClick={handleSaveAssignments}
          disabled={isSubmitting || assignedCount === 0}
          className="flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          {isSubmitting ? "Saving..." : `Save Assignments (${assignedCount})`}
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Event Info */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Event Information</CardTitle>
              <CardDescription>Event details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">Event:</span>
                <span>{event.name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">Format:</span>
                <span>{event.format}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">Date:</span>
                <span>{event.date}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">Unassigned Standings:</span>
                <Badge variant="secondary">{totalUnassigned}</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Unassigned Standings */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Unassigned Standings</CardTitle>
                  <CardDescription>
                    Assign users to standings that don't have users assigned
                  </CardDescription>
                </div>
                <div className="text-sm">
                  <span className="text-green-600">
                    {assignedCount} assigned
                  </span>
                  {totalUnassigned > assignedCount && (
                    <span className="text-red-600 ml-2">
                      {totalUnassigned - assignedCount} unassigned
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {unassignedStandings.length > 0 ? (
                <div className="space-y-4">
                  {unassignedStandings.map((standing: any) => (
                    <Card key={standing.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="text-lg font-bold">
                              #{standing.rank}
                            </div>
                            <div>
                              <div className="font-medium">
                                {standing.playerName || `Rank ${standing.rank}`}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {standing.points} points • OMW: {standing.omw}%
                                • GW: {standing.gw}% • OGW: {standing.ogw}%
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Select
                              value={selectedUsers[standing.id] || ""}
                              onValueChange={(value) =>
                                handleUserSelection(standing.id, value)
                              }
                            >
                              <SelectTrigger className="w-64">
                                <SelectValue placeholder="Select user..." />
                              </SelectTrigger>
                              <SelectContent>
                                {allUsers?.map((user: any) => (
                                  <SelectItem key={user.id} value={user.id}>
                                    <div className="flex flex-col">
                                      <span>
                                        {user.displayName || user.username}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        {user.email}
                                      </span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    No Unassigned Standings
                  </h3>
                  <p className="text-muted-foreground">
                    All standings have been assigned to users.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EventStandingsAssignment;
